import { createContext, useContext, useEffect } from 'react';

/**
 * SEO metadata for a single route.
 *
 * The same declaration serves two consumers: during prerendering it is collected
 * and written into the static `<head>`; in the browser it is applied to the live
 * document on navigation. React 19 hoists `<title>`/`<meta>` rendered in
 * components, but that hoisting is client-side only — for crawler-visible static
 * HTML the tags must be injected at build time, which is what this indirection
 * buys us.
 */
export interface SeoData {
  title: string;
  description: string;
  /** Absolute or root-relative canonical path for this route. */
  canonical?: string;
  /** Locale alternates as `{ en: '/trips/x', es: '/es/viajes/x' }`. */
  alternates?: Record<string, string>;
  image?: string;
  /** Structured data objects serialised into `application/ld+json` blocks. */
  jsonLd?: Record<string, unknown>[];
  noindex?: boolean;
}

export interface SeoCollector {
  collect: (data: SeoData) => void;
}

export const SeoContext = createContext<SeoCollector | null>(null);

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string, hreflang?: string): void {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    if (hreflang) el.hreflang = hreflang;
    document.head.appendChild(el);
  }
  el.href = href;
}

/** Applies metadata to the live document. Browser only. */
export function applySeoToDocument(data: SeoData): void {
  document.title = data.title;
  upsertMeta('name', 'description', data.description);
  upsertMeta('property', 'og:title', data.title);
  upsertMeta('property', 'og:description', data.description);
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('name', 'robots', data.noindex ? 'noindex,nofollow' : 'index,follow');
  if (data.image) upsertMeta('property', 'og:image', data.image);
  if (data.canonical) upsertLink('canonical', data.canonical);
  for (const [locale, href] of Object.entries(data.alternates ?? {})) {
    upsertLink('alternate', href, locale);
  }
}

/**
 * Declares the SEO metadata for the current route.
 *
 * On the server the data is handed to the collector supplied by the prerenderer;
 * in the browser it is written to the document after paint.
 */
export function useSeo(data: SeoData): void {
  const collector = useContext(SeoContext);
  // Prerender pass: capture synchronously during render, since effects never run.
  collector?.collect(data);

  const serialised = JSON.stringify(data);
  useEffect(() => {
    applySeoToDocument(JSON.parse(serialised) as SeoData);
  }, [serialised]);
}

/** Renders collected metadata as the HTML injected into a prerendered `<head>`. */
export function renderSeoToHtml(data: SeoData, siteName: string): string {
  const esc = (value: string): string =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const tags = [
    `<title>${esc(data.title)}</title>`,
    `<meta name="description" content="${esc(data.description)}" />`,
    `<meta name="robots" content="${data.noindex ? 'noindex,nofollow' : 'index,follow'}" />`,
    `<meta property="og:site_name" content="${esc(siteName)}" />`,
    `<meta property="og:title" content="${esc(data.title)}" />`,
    `<meta property="og:description" content="${esc(data.description)}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
  ];

  if (data.image) tags.push(`<meta property="og:image" content="${esc(data.image)}" />`);
  if (data.canonical) tags.push(`<link rel="canonical" href="${esc(data.canonical)}" />`);

  for (const [locale, href] of Object.entries(data.alternates ?? {})) {
    tags.push(`<link rel="alternate" hreflang="${esc(locale)}" href="${esc(href)}" />`);
  }

  for (const block of data.jsonLd ?? []) {
    // `</script>` inside JSON-LD would terminate the block early.
    const json = JSON.stringify(block).replace(/</g, '\\u003c');
    tags.push(`<script type="application/ld+json">${json}</script>`);
  }

  return tags.join('\n    ');
}
